import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { MaskedLines, Reveal, FadeIn } from "@/components/Motion";
import BrandStrip from "@/components/BrandStrip";
import { STONES } from "@/data/stones";
import { useSeo } from "@/hooks/useSeo";

function StoneRow({ stone, index }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-8%", "8%"]);
  const flip = index % 2 === 1;

  return (
    <div
      ref={ref}
      data-testid={`stone-row-${stone.slug}`}
      className="grid lg:grid-cols-12 gap-12 lg:gap-20 items-center py-14 md:py-28 border-b border-border last:border-b-0"
    >
      <div className={`lg:col-span-6 ${flip ? "lg:order-2" : ""}`}>
        <Reveal>
          <div className={`relative overflow-hidden ${stone.aspect} ${index % 3 === 2 ? "lg:aspect-[16/9]" : ""}`}>
            <motion.img
              src={stone.image}
              alt={stone.name}
              style={{ y }}
              className="w-full h-[115%] object-cover transition-transform duration-700 hover:scale-[1.03]"
            />
            {stone.signature && (
              <span className="absolute top-5 left-5 bg-background/90 backdrop-blur px-4 py-2 text-[10px] tracking-[0.25em] uppercase">
                Signature
              </span>
            )}
          </div>
        </Reveal>
      </div>
      <div className={`lg:col-span-6 ${flip ? "lg:order-1 lg:text-right lg:justify-items-end" : ""}`}>
        <Reveal>
          <p className="font-serif text-7xl md:text-8xl font-light text-secondary-foreground/20 leading-none mb-6">
            {stone.number}
          </p>
          <h2 className="font-serif text-4xl sm:text-5xl font-light tracking-tight">
            <Link to={`/collections/${stone.slug}`} className="transition-colors duration-300 hover:text-foreground/60">
              {stone.name}
            </Link>
          </h2>
          <p className="mt-3 text-xs tracking-[0.25em] uppercase text-secondary-foreground">{stone.origin}</p>
          <p className={`mt-7 text-base font-light leading-relaxed text-muted-foreground max-w-md ${flip ? "lg:ml-auto" : ""}`}>
            {stone.description}
          </p>
          <div className={`mt-8 flex flex-wrap gap-3 ${flip ? "lg:justify-end" : ""}`}>
            {stone.finishes.map((f) => (
              <span key={f} className="text-xs tracking-[0.15em] uppercase border border-border px-4 py-2 text-secondary-foreground">
                {f}
              </span>
            ))}
          </div>
          <div className={`mt-10 flex flex-wrap items-center gap-x-8 gap-y-4 ${flip ? "lg:justify-end" : ""}`}>
            <Link
              to={`/collections/${stone.slug}`}
              data-testid={`stone-explore-${stone.slug}`}
              className="group inline-flex items-center gap-3 text-xs tracking-[0.2em] uppercase"
            >
              <span className="border-b border-foreground pb-1">Explore the collection</span>
              <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
            <Link
              to={`/contact?stone=${stone.slug}`}
              data-testid={`stone-enquire-${stone.slug}`}
              className="group inline-flex items-center gap-3 text-xs tracking-[0.2em] uppercase text-secondary-foreground hover:text-foreground transition-colors"
            >
              <span className="border-b border-foreground/40 pb-1 transition-colors duration-300 group-hover:border-foreground">
                Enquire
              </span>
            </Link>
          </div>
        </Reveal>
      </div>
    </div>
  );
}

export default function Collections() {
  useSeo({
    title: "Stone Collections — Quartz, Marble, Granite & Onyx | Varun Marbles",
    description:
      "Browse seven families of stone: quartz slabs, Italian marble, Indian marble, granite, imported granite and onyx — curated at the quarry by Varun Marbles, Gurgaon.",
    path: "/collections",
  });
  return (
    <>
      <section className="max-w-7xl mx-auto px-6 md:px-12 pt-32 md:pt-52 pb-14 md:pb-24">
        <FadeIn delay={0.1}>
          <p data-testid="collections-overline" className="text-xs tracking-[0.3em] uppercase text-secondary-foreground mb-8">
            The collections — Seven families
          </p>
        </FadeIn>
        <h1 data-testid="collections-headline" className="font-serif font-light tracking-tighter leading-[0.95] text-5xl sm:text-6xl lg:text-8xl">
          <MaskedLines
            delay={0.2}
            lines={[<>Every slab,</>, <><span className="italic">a landscape.</span></>]}
          />
        </h1>
        <FadeIn delay={0.8}>
          <p className="mt-10 max-w-lg text-base font-light leading-relaxed text-muted-foreground">
            From our signature quartz slabs to backlit imported onyx — each collection is curated
            at the quarry and finished for your space.
          </p>
        </FadeIn>
      </section>

      <BrandStrip />

      <section data-testid="collections-list" className="max-w-7xl mx-auto px-6 md:px-12 pb-28 md:pb-40">
        {STONES.map((stone, i) => (
          <StoneRow key={stone.slug} stone={stone} index={i} />
        ))}
      </section>
    </>
  );
}
